import { z } from "zod";

/**
 * Merkezi katalog 2. aşama (v1.2): panelden Trendyol'a ürün açma, güncelleme ve onay takibi.
 * Trendyol içeriği (başlık, kategori, marka, özellikler) ürün kartının Trendyol sekmesinde hazırlanır.
 */

export const TRENDYOL_STATUS_LABELS = {
  not_listed: "Trendyol'da yok",
  draft: "Taslak",
  pending: "İşleniyor",
  review: "Onay bekliyor",
  rejected: "Reddedildi",
  synced: "Yayında",
  dirty: "Gönderilmedi",
  drifted: "Kanalda farklı",
  failed: "Gönderilemedi",
} as const;
export type TrendyolStatus = keyof typeof TRENDYOL_STATUS_LABELS;

/** Trendyol'un kabul ettiği KDV oranları. */
export const TRENDYOL_VAT_RATES = [0, 1, 10, 20] as const;

export interface TrendyolAttributeValue {
  attributeId: number;
  valueIds?: number[];
  value?: string;
}

export interface TrendyolDraft {
  title: string;
  /** null: panel açıklaması kullanılır. */
  description: string | null;
  categoryId: number | null;
  brandId: number | null;
  brandName: string | null;
  barcode: string;
  productMainId: string;
  stockCode: string;
  vatRate: number;
  desi: number;
  attributes: TrendyolAttributeValue[];
}

export interface TrendyolAttributeDto {
  id: number;
  name: string;
  required: boolean;
  allowCustom: boolean;
  multiple: boolean;
  values: { id: number; name: string }[];
  /** Panel özelliklerinden veya kategorideki son seçimden önerilen değer (taslakta yoksa). */
  suggestion: TrendyolAttributeValue | null;
}

export interface TrendyolTabDto {
  connected: boolean;
  status: TrendyolStatus;
  /** Ürün Trendyol'da onaylıysa. */
  listing: { contentId: string; productUrl: string | null; onSale: boolean } | null;
  draft: TrendyolDraft;
  /** Taslak kaydedilmiş mi (değilse panelden önerildi). */
  saved: boolean;
  category: { id: number; path: string } | null;
  attributes: TrendyolAttributeDto[];
  /** Göndermeyi engelleyen eksikler. */
  issues: string[];
  warnings: string[];
  price: { list: string; sale: string; percent: number } | null;
  quantity: number | null;
  images: string[];
  rejectReasons: string[];
  lastError: string | null;
  lastPublishedAt: string | null;
  lastCheckedAt: string | null;
  /** Onaylı üründe panel ile Trendyol arasındaki farklı alanlar. */
  diff: { field: string; label: string; panel: string; channel: string }[];
  /** Çok varyantlı üründe Trendyol'a gidecek varyantlar (tek varyantta boş). */
  variants: { sku: string; label: string; barcode: string; price: string | null; quantity: number | null; attributes: string }[];
  canEdit: boolean;
}

const attributeValueSchema = z.object({
  attributeId: z.number().int().positive(),
  valueIds: z.array(z.number().int().positive()).max(20).optional(),
  value: z.string().trim().max(200).optional(),
});

export const trendyolDraftSchema = z.object({
  title: z.string().trim().min(5, "Başlık en az 5 karakter").max(100, "Trendyol başlığı en fazla 100 karakter"),
  description: z.string().max(30_000, "Açıklama en fazla 30.000 karakter").nullable(),
  categoryId: z.number().int().positive().nullable(),
  brandId: z.number().int().positive().nullable(),
  brandName: z.string().max(120).nullable(),
  barcode: z
    .string()
    .trim()
    .min(1, "Barkod girin")
    .max(40, "Barkod en fazla 40 karakter")
    .regex(/^[A-Za-z0-9._-]+$/, "Barkodda yalnız harf, rakam ve . - _ olabilir"),
  productMainId: z.string().trim().min(1, "Model kodu girin").max(40),
  stockCode: z.string().trim().min(1, "Stok kodu girin").max(100),
  vatRate: z.union([z.literal(0), z.literal(1), z.literal(10), z.literal(20)]),
  desi: z.number().min(0).max(1000),
  attributes: z.array(attributeValueSchema).max(100),
});
export type TrendyolDraftInput = z.infer<typeof trendyolDraftSchema>;

export const trendyolSettingsSchema = z.object({
  vatRate: z.union([z.literal(0), z.literal(1), z.literal(10), z.literal(20)]),
  desi: z.number().min(0).max(1000),
  /** 0: aynı gün kargo, 1: en geç ertesi gün; null: Trendyol'daki varsayılan. */
  deliveryDuration: z.number().int().min(0).max(30).nullable(),
  shipmentAddressId: z.number().int().positive().nullable(),
  returningAddressId: z.number().int().positive().nullable(),
  /** Kanal fiyat kuralı kapalıyken IdeaSoft fiyatına eklenecek yüzde. */
  markupPercent: z.number().min(0).max(90),
  /** IdeaSoft kategorisi eşleşmemiş ürünlerde önerilen Trendyol kategorisi (ör. Kol Saati). */
  defaultCategoryId: z.number().int().positive().nullable().default(null),
});
export type TrendyolSettings = z.infer<typeof trendyolSettingsSchema>;
export const TRENDYOL_DEFAULT_SETTINGS: TrendyolSettings = { vatRate: 20, desi: 1, deliveryDuration: null, shipmentAddressId: null, returningAddressId: null, markupPercent: 0, defaultCategoryId: null };

/** Göndermeyi engelleyen eksiklerin türü (toplu gönderimde türe göre topluca tamamlanır). */
export const TRENDYOL_ISSUE_KINDS = ["category", "brand", "description", "images", "price", "attributes", "variant", "other"] as const;
export type TrendyolIssueKind = (typeof TRENDYOL_ISSUE_KINDS)[number];
export const TRENDYOL_ISSUE_LABELS: Record<TrendyolIssueKind, string> = {
  category: "Trendyol kategorisi seçilmemiş",
  brand: "Trendyol markası bulunamadı",
  description: "Açıklama boş",
  images: "Görsel eksik ya da geçersiz",
  price: "IdeaSoft fiyatı yok",
  attributes: "Zorunlu özellik eksik",
  variant: "Varyant bilgisi eksik",
  other: "Diğer",
};

export interface TrendyolPreflightItem {
  id: string;
  name: string;
  /** ready: eksiksiz, gönderilebilir · blocked: eksik var. */
  state: "ready" | "blocked";
  issues: { kind: TrendyolIssueKind; message: string }[];
  /** Kontrol sırasında kendiliğinden tamamlananlar (IdeaSoft'tan açıklama / görsel, https, varsayılan kategori). */
  filled: string[];
}
export interface TrendyolPreflightDto {
  items: TrendyolPreflightItem[];
}
export const trendyolPreflightSchema = z.object({ productIds: z.array(z.string().max(40)).min(1).max(25) });

/** Eksikleri toplu tamamlama: kategori ata, boş açıklamayı üret. Yalnız Trendyol'da henüz olmayan ürünlere uygulanır. */
export const trendyolFillSchema = z.object({
  productIds: z.array(z.string().max(40)).min(1).max(200),
  /** Kategorisi boş ürünlere atanır. */
  categoryId: z.number().int().positive().optional(),
  /** Atanan kategoriyi, ürünlerin IdeaSoft kategorilerine de eşle (sonraki ürünlerde kendiliğinden gelsin). */
  rememberCategory: z.boolean().default(false),
  /** Açıklaması boş ürünlere ürün adı ve özelliklerinden açıklama üret. */
  description: z.boolean().default(false),
});
export interface TrendyolFillResult {
  categorySet: number;
  descriptionSet: number;
  mapped: number;
  skipped: number;
}

/** IdeaSoft kategori numarası → Trendyol kategori numarası (null: eşleştirme kaldırılır). */
export const trendyolCategoryMapSchema = z.record(z.string().max(20), z.number().int().positive().nullable());

export const trendyolBulkPublishSchema = z.object({ productIds: z.array(z.string().max(40)).min(1).max(200) });

export interface TrendyolOptionsDto {
  connected: boolean;
  categories: { id: number; path: string }[];
  addresses: { id: number; type: "shipment" | "returning" | "invoice"; label: string; isDefault: boolean }[];
  settings: TrendyolSettings;
  /** Kanal fiyat kuralı açıksa Trendyol fiyatı oradan hesaplanır. */
  priceRule: { enabled: boolean; percent: number };
  categoryMap: Record<string, number>;
  /** Ürünlerde kullanılan IdeaSoft kategorileri (eşleştirme ekranı için). */
  ideasoftCategories: { id: string; path: string; products: number }[];
  error: string | null;
}

export interface TrendyolBulkJobDto {
  id: string;
  status: string;
  total: number;
  done: number;
  ok: number;
  failed: number;
  errors: { name: string; message: string }[];
  createdAt: string;
}
